import { useState, useEffect, useRef } from 'react';
import { DiwaliBadge } from './DiwaliBadge';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Hexagon, Layers, Gauge, BadgeCheck } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { TrustBadges } from './TrustBadges';
import { ProductWordmark } from './ProductWordmark';
import { PaddleStage } from './PaddleStage';
import { LaunchCountdown, LaunchedBanner } from './LaunchCountdown';
import { useLaunchTimer } from '../hooks/useLaunchTimer';
import { AmazonBuyButton } from './AmazonBuy';
import { COMPANY } from '../data/legalContent';
import { fbTrackDual } from '../lib/pixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All ORIGINAL untouched product photos — presented studio-clean, never altered
const VIEWS = [
  { key: 'full', label: 'Full view', src: `${API}/static/products/hexcore_n1.png` },
  { key: 'face', label: 'Face', src: `${API}/static/products/hexcore_n2.png` },
  { key: 'side', label: 'Side profile', src: `${API}/static/products/hexcore_n5.png` },
  { key: 'grip', label: 'Grip', src: `${API}/static/products/hexcore_n3b.png` },
  { key: 'handle', label: 'Handle', src: `${API}/static/products/hexcore_n4.png` },
  { key: 'detail', label: 'Detail', src: `${API}/static/products/hexcore_n6.png`, fade: true },
  { key: 'sleeve', label: 'Pro Series', src: `${API}/static/products/hexcore_sleeve.jpg`, fade: true },
];

const SPECS = [
  { Icon: Hexagon, label: 'Core', value: '16mm PP Honeycomb' },
  { Icon: Layers, label: 'Face', value: 'Toray T700 Carbon Fibre' },
  { Icon: Gauge, label: 'Weight', value: '7.6 – 8.0 oz' },
  { Icon: BadgeCheck, label: 'Certified', value: 'USA Pickleball Approved' },
];

const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, shown];
};

export const HexCoreShowcase = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [ref, shown] = useReveal();

  useEffect(() => {
    fetch(`${API}/products/hexcore`)
      .then((r) => r.json())
      .then(setProduct)
      .catch(() => {});
  }, []);

  const soldOut = product?.in_stock === false;
  const timer = useLaunchTimer();
  const viewedRef = useRef(false);
  useEffect(() => {
    if (!product || viewedRef.current) return;
    viewedRef.current = true;
    fbTrackDual('ViewContent', {
      content_type: 'product', content_ids: [String(product.slug)], content_name: product.name,
      currency: 'INR', value: product.priceINR,
      contents: [{ id: String(product.slug), quantity: 1, item_price: product.priceINR }],
    });
  }, [product]);
  const reveal = (delay) => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  return (
    <section
      id="shop"
      ref={ref}
      className="relative bg-white overflow-hidden isolate"
      aria-label="DYSSC HexCore Pro — flagship pickleball paddle"
      data-testid="hexcore-showcase"
    >
      {/* studio noise — breaks digital flatness */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.025]" style={{ backgroundImage: NOISE }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 md:pt-8 pb-12 md:pb-16 relative" data-testid="product-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-5 lg:gap-y-0 lg:gap-x-6">

          {/* ---- Header (mobile: above images) ---- */}
          <div className="lg:col-span-5 lg:col-start-1 lg:row-start-1 lg:self-end relative z-20">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#E02128] font-bold" style={reveal(80)}>
              Flagship · USA Pickleball Approved
            </p>

            <h2 className="mt-5" style={reveal(160)}>
              <img
                src={`${API}/static/brand/dyssc_wordmark_dark.png`}
                alt="DYSSC"
                className="h-4 md:h-5 w-auto"
              />
              <span className="relative inline-block">
                <ProductWordmark
                  slug="hexcore"
                  name="DYSSC HexCore Pro"
                  imgClassName="h-12 md:h-14 mt-2"
                  fallbackClassName="font-display text-5xl md:text-7xl text-[#0A0A0A] uppercase tracking-tight"
                />
              </span>
            </h2>
            <p className="mt-3 text-[#737373] text-base md:text-lg leading-relaxed max-w-md" style={reveal(240)}>
              Energy-responsive HoneyComb structure. Lighter weight, surgical control —
              built with aerospace-grade Toray T700 carbon fibre.
            </p>
          </div>

          {/* ---- Interactive render stage ---- */}
          <div className="lg:col-span-7 lg:col-start-6 lg:row-start-1 lg:row-span-2 lg:self-center relative" style={reveal(0)}>
            {/* massive ghost typography behind the product */}
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none z-0" aria-hidden="true">
              <span
                className="font-display font-black uppercase leading-none tracking-tighter text-[clamp(80px,10.5vw,168px)] text-transparent"
                style={{ WebkitTextStroke: '1.5px #E8E8EA' }}
              >
                HEX
              </span>
              <span className="font-display font-black uppercase leading-none tracking-tighter text-[clamp(80px,10.5vw,168px)] text-[#F2F2F4]">
                CORE
              </span>
            </div>

            <div className="relative z-10">
              <PaddleStage
                views={VIEWS}
                alt="DYSSC HexCore Pro pickleball paddle"
                autoCycle={shown}
                showHint
                onStageClick={() => navigate('/product/hexcore')}
                heightClass="h-[420px] md:h-[600px]"
              />
            </div>
          </div>

          {/* ---- Price, specs & CTAs (mobile: below images) ---- */}
          <div className="lg:col-span-5 lg:col-start-1 lg:row-start-2 lg:self-start relative z-20">
            <div className="lg:mt-6 lg:pt-5 lg:border-t border-neutral-200" style={reveal(320)}>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 flex items-center gap-2">
                Price · Incl. GST
                {soldOut && (
                  <span
                    className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest text-[#E02128] bg-[#E02128]/10 border border-[#E02128]/40"
                    data-testid="sold-out-badge"
                  >
                    SOLD OUT
                  </span>
                )}
              </p>
              <div className="mt-2 flex items-end gap-3 flex-wrap">
                <span className="font-mono text-4xl md:text-5xl text-[#0A0A0A]" data-testid="grid-product-price">
                  {product ? `₹${product.priceINR.toLocaleString('en-IN')}` : '—'}
                </span>
                {product?.compareAtINR > product?.priceINR && (
                  <>
                    <span className="font-mono text-xl text-neutral-400 line-through mb-1" data-testid="grid-compare-price">
                      ₹{product.compareAtINR.toLocaleString('en-IN')}
                    </span>
                    <span className="mb-1.5 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wider text-white bg-gradient-to-r from-[#E02128] via-[#FF5A60] to-[#E02128] bg-[length:200%_100%] animate-[gradient-x_2.5s_linear_infinite] shadow-[0_4px_16px_rgba(224,33,40,0.35)]">
                      {product.discountPercent}% OFF
                    </span>
                  </>
                )}
                {product && <DiwaliBadge className="mb-1.5" testId="diwali-offer-badge-hexcore" />}
              </div>
            </div>

            {/* spec grid — 2×2 technical tiles */}
            <div className="mt-5 grid grid-cols-2 gap-px bg-neutral-200 border border-neutral-200" style={reveal(400)} data-testid="showcase-specs">
              {SPECS.map(({ Icon, label, value }) => (
                <div key={label} className="bg-white p-4 group hover:bg-[#FAFAFA] transition-colors duration-300">
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-[#E02128]" strokeWidth={1.8} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{label}</span>
                  </div>
                  <p className="mt-1.5 text-sm font-semibold text-[#0A0A0A] leading-snug">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6" style={reveal(480)}>
              {timer.counting ? (
                <LaunchCountdown variant="showcase" slug="hexcore" />
              ) : soldOut && timer.launched ? (
                <LaunchedBanner slug="hexcore" compact />
              ) : (
              <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={() => {
                  if (!product) return;
                  if (soldOut) { navigate('/notify/hexcore'); return; }
                  addToCart(product, 1);
                }}
                className="inline-flex items-center justify-center w-[212px] px-9 py-4 rounded-full bg-[#E02128] hover:bg-[#B81A20] text-white font-bold uppercase tracking-[0.15em] text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] shadow-[0_12px_30px_rgba(224,33,40,0.28)] hover:shadow-[0_0_24px_rgba(224,33,40,0.45)]"
                data-testid="add-to-cart-button"
              >
                {soldOut ? 'Notify Me' : 'Add to Cart'}
              </button>
              <button
                onClick={() => navigate('/product/hexcore')}
                className="group inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#E02128] text-sm font-semibold uppercase tracking-[0.15em] transition-colors duration-200"
                data-testid="showcase-view-details"
              >
                Full details
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </button>
              </div>
              )}
              {!timer.counting && !soldOut && (
                <div className="mt-4" style={reveal(520)}>
                  <AmazonBuyButton url={COMPANY.amazon.hexcore} placement="showcase" variant="pill" />
                </div>
              )}
              {(timer.counting || (soldOut && timer.launched)) && (
                <button
                  onClick={() => navigate('/product/hexcore')}
                  className="group mt-4 inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#E02128] text-sm font-semibold uppercase tracking-[0.15em] transition-colors duration-200"
                  data-testid="showcase-view-details"
                >
                  Full details
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              )}
            </div>

            <div className="mt-4 max-w-md" style={reveal(560)}>
              <TrustBadges />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
